import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../../util/API";
import { Badge, Button, Card, Field, Input, TextArea } from "./mailUI";

const parseVariables = (value) =>
  String(value || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

const EmailTemplateDetail = () => {
  const navigate = useNavigate();
  const { key } = useParams();
  const isCreate = key === "new";

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [template, setTemplate] = useState(null);
  const [groups, setGroups] = useState([]);

  const [form, setForm] = useState({
    key: "",
    name: "",
    group: "General",
    subject: "",
    text: "",
    html: "",
    description: "",
    variablesText: "",
  });

  const loadGroups = async () => {
    try {
      const res = await API.get("/mail/template-groups");
      setGroups(res.data?.data?.items || []);
    } catch {
      setGroups([]);
    }
  };

  const loadTemplate = async () => {
    if (isCreate) return;
    setLoading(true);
    setError("");
    try {
      const res = await API.get(`/mail/templates/${key}`);
      const data = res.data?.data || null;
      setTemplate(data);
      setForm({
        key: data.key || "",
        name: data.name || "",
        group: data.group || "General",
        subject: data.subject || "",
        text: data.text || "",
        html: data.html || "",
        description: data.description || "",
        variablesText: Array.isArray(data.variables) ? data.variables.join(", ") : "",
      });
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load template"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
    if (isCreate) {
      setTemplate(null);
    } else {
      loadTemplate();
    }
  }, [key]);

  const allGroupNames = useMemo(() => {
    const names = new Set();
    groups.forEach((g) => names.add(String(g.name || "").trim()));
    if (template?.group) names.add(String(template.group));
    names.add("General");
    return Array.from(names).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [groups, template]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    const payload = {
      key: isCreate ? form.key || undefined : undefined,
      name: form.name,
      group: form.group,
      subject: form.subject,
      text: form.text,
      html: form.html,
      description: form.description,
      variables: parseVariables(form.variablesText),
    };

    try {
      if (isCreate) {
        await API.post("/mail/templates", payload);
        navigate("/administrator/mail-management/templates");
      } else {
        await API.put(`/mail/templates/${form.key}`, payload);
        await loadTemplate();
      }
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save template"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!template?.key) return;
    setSaving(true);
    setError("");
    try {
      await API.delete(`/mail/templates/${template.key}`);
      navigate("/administrator/mail-management/templates");
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to delete template"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card
        title={isCreate ? "Create Template" : "Template Editor"}
        subtitle="Edit template content and preview the HTML output."
        right={
          !isCreate && template?.source ? (
            <Badge tone={template.source === "custom" ? "info" : "warning"}>
              {template.source === "override" ? "Edited Default" : template.source}
            </Badge>
          ) : null
        }
      >
        {loading ? (
          <p className="text-sm text-slate-500">Loading template...</p>
        ) : error ? (
          <p className="text-sm text-rose-600">{error}</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Template Key" hint={isCreate ? "Leave blank to auto-generate." : "Key is locked."}>
              <Input
                value={form.key}
                onChange={(event) => setForm((prev) => ({ ...prev, key: event.target.value }))}
                placeholder="order-confirmation"
                disabled={!isCreate}
              />
            </Field>
            <Field label="Template Name">
              <Input
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Order Confirmation"
              />
            </Field>
            <Field label="Group">
              <select
                value={form.group}
                onChange={(event) => setForm((prev) => ({ ...prev, group: event.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900"
              >
                {allGroupNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Subject">
              <Input
                value={form.subject}
                onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                placeholder="Your order {{orderNumber}} is confirmed"
              />
            </Field>
            <Field label="Text Content">
              <TextArea
                value={form.text}
                onChange={(event) => setForm((prev) => ({ ...prev, text: event.target.value }))}
                placeholder="Plain text content"
              />
            </Field>
            <Field label="HTML Content">
              <TextArea
                value={form.html}
                onChange={(event) => setForm((prev) => ({ ...prev, html: event.target.value }))}
                placeholder="<p>HTML content</p>"
              />
            </Field>
            <Field label="Variables" hint="Comma-separated variable names.">
              <Input
                value={form.variablesText}
                onChange={(event) => setForm((prev) => ({ ...prev, variablesText: event.target.value }))}
                placeholder="customerName, orderNumber"
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="What this template is used for"
              />
            </Field>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-3">
          <Button onClick={handleSave} disabled={saving || !form.name || !form.subject}>
            {saving ? "Saving..." : isCreate ? "Create Template" : "Save Changes"}
          </Button>
          {!isCreate && template?.canDelete ? (
            <Button variant="danger" onClick={handleDelete} disabled={saving}>
              Delete Template
            </Button>
          ) : null}
          {!isCreate && template?.canRevert ? (
            <Button variant="secondary" onClick={handleDelete} disabled={saving}>
              Revert to Default
            </Button>
          ) : null}
        </div>
      </Card>

      <Card title="HTML Preview" subtitle="Rendered email content preview.">
        {form.html ? (
          <div
            className="rounded-2xl border border-slate-200 bg-white p-6"
            dangerouslySetInnerHTML={{ __html: form.html }}
          />
        ) : (
          <p className="text-sm text-slate-500">Add HTML content to preview the email.</p>
        )}
      </Card>
    </div>
  );
};

export default EmailTemplateDetail;
